import { action, override, computed, observable, makeObservable } from 'mobx'

import { PAYMENT_FORMS, PAYMENT_PROVIDERS } from 'constants/paymentSettingShared.constants'
import { isNotOneTimePlanForm } from 'constants/pricingPlans.constants'
import { DEFAULT_CURRENCY } from 'constants/currencies.constants'
import { SUCCESSFUL_ORDER_STATES } from 'constants/ordersShared.constants'
import { YES } from '@elo-kit/constants/general.constants'

import { arrayLast } from 'utils/array.utils'
import { toAlpha2CountryCode } from 'utils/countries.utils'

import {
  convertPriceForStripe,
  handleStripeCardTransaction,
  handleTransactionResponse,
  handleStripeCardRecuringPayment,
  handleAdditionalConfirmation,
  handleStripePaymentElementTransaction,
  handleStripePaymentElementSetup,
  type StripeBillingDetails,
  getStripeLocale,
} from '@elo-kit/utils/stripe.utils'
import { getPubKey } from 'utils/paymentSettingShared.utils'

import { notify } from 'libs/common/notify'
import { apiClient, getMP3dsBrowserMetaData } from 'utils/requests.utils'
import { isWindowEnv } from 'utils/env.utils'

import type { Nullable } from 'types/helpers'

import SharedStore from 'shared/stores/shared.store'
import { ShopRootStore } from 'shop/stores/shopRoot.store'

import { OrdersApi, createOrdersApi } from 'shop/api/orders.api'
import type { Details, Invoice, Order, Paymethods, Popups, Transfer } from 'shop/types/orders'
import { TicketUpgradeData } from 'types/upgrade-mappings'
import type { Stripe, StripeElements, StripeExpressCheckoutElement, StripeElementsOptionsMode } from '@stripe/stripe-js'
import type { StripePaymentParams, ClickSubmitData } from 'shop/types/stripe'
import { createKibanaApi, KibanaApi } from 'shop/api/kibana.api'
import * as Sentry from '@sentry/react'
import { capitalizeFirstLetter } from 'utils/nameStyle.utils'
import { COUNTRIES_WITH_STATES } from 'constants/countries.constants'
import { isSetupIntentPossible } from 'shared/components/purchase-methods/expressCheckout.utils'
import {
  PAYMENT_ELEMENTS_MODE,
  StripeElementsPayloads,
} from '../../interfaces/payment-method-interfaces/StripePaymentMethod'

import {
  CreateCardRegistrationResult,
  MangoPayCardPayment,
} from '../../interfaces/payment-method-interfaces/MangoPayCardPayment'

interface ChargeOrderData {
  pricingPlanId: number
  transfer?: {
    paypalFraudToken?: string
    browserInfo?: {
      colorDepth: number
      screenHeight: number
      screenWidth: number
      timeZoneOffset: number
      language: string
      javaEnabled: boolean
      javaScriptEnabled: boolean
      userAgent: string
    }
  }
}

const methodsToSubmit = [
  PAYMENT_FORMS.klarna,
  PAYMENT_FORMS.p24,
  PAYMENT_FORMS.ideal,
  PAYMENT_FORMS.card,
  PAYMENT_FORMS.mondu,
  PAYMENT_FORMS.twint,
  PAYMENT_FORMS.payByBank,
  PAYMENT_FORMS.sepa,
]

export class OrdersStore extends SharedStore<Order> implements MangoPayCardPayment, StripeElementsPayloads {
  storeName = 'OrdersStore'
  declare childApi: OrdersApi
  kibanaApi: KibanaApi
  root: ShopRootStore

  constructor(root: ShopRootStore) {
    super()
    this.root = root
    this.childApi = createOrdersApi(root?.apiClient ?? apiClient)
    makeObservable(this)
    this.kibanaApi = createKibanaApi(root?.apiClient ?? apiClient)
    this.loading = true
  }

  @observable data: Details
  @observable selectedPlanId: Nullable<number> = null
  @observable paymethods: Paymethods
  @observable currentFunnelNode = {}
  @observable popups: Popups = {
    changePlan: false,
    changeMethod: false,
    changePlanAutopay: false,
    postsellPayment: false,
    autopaySecondStep: false,
    changeAttendeeDetails: false,
  }

  @observable stripePaymentParams: StripePaymentParams = {
    clientSecret: null,
    redirectionUrl: null,
    payerFullName: null,
    payerCountryCode: null,
    payerEmail: null,
    setupIntentSecret: null,
  }
  @observable fraudSessionIdentifier = null
  @observable purchaseError = ''

  @observable ticketUpgradeData: Nullable<TicketUpgradeData> = null

  @observable eventAttendees = null

  @observable mangoPayForm = null

  @action setMangoPayForm = (form) => (this.mangoPayForm = form)

  @computed get seller() {
    return this.root.sellerStore.item
  }

  @action setTokenAndFetchPS = async (
    token: string,
    data?: { expand: string[] },
    params?: { token?: string; username?: string },
    accessToken?: string
  ) => {
    this.data = {
      ...this.data,
      token,
      params: isWindowEnv() ? this.getAllUrlParams() : params,
    }

    return await this.fetchPSManage(data, params?.username, accessToken)
  }

  @action fetchEventAttendees = async (token: string, data: { username: string }) => {
    this.setLoading(true)
    const resp = await this.childApi.fetchEventAttendees(token, data)
    this.setLoading(false)
    this.eventAttendees = resp?.data
    return resp
  }

  @action fetchTicketUpgrade = async (token: string, data: { username: string }) => {
    this.setLoading(true)
    const resp = await this.childApi.fetchTicketUpgrade(token, data)
    this.setLoading(false)
    this.ticketUpgradeData = resp?.data
    return resp
  }

  @action togglePopup = (name: string) => {
    this.popups[name] = !this.popups[name]
  }

  @action setLoading = (value) => {
    this.loading = value
  }

  @action hideLoading = () => this.setLoading(false)

  @action setStripePaymentParams = (params: StripePaymentParams) => {
    this.stripePaymentParams = params
  }

  @action updatePaymethodsData = (data: Paymethods) => {
    this.paymethods = data
  }

  @action onSelectPlan = (planId: number) => {
    this.selectedPlanId = planId
  }

  @action setFraudSessionIdentifier = (value) => {
    this.fraudSessionIdentifier = value
  }

  @action setPurchaseError = (error: string) => {
    this.purchaseError = error
  }

  @action sendDealPaymentEmail = async () => {
    this.toggleLoading(true)
    const resp = await this.childApi.sendDealPaymentEmail(this.root.sellerStore.item.username, this.item.token)
    this.toggleLoading(false)
    return resp
  }

  @action sendDealBindingOfferEmail = async () => {
    this.toggleLoading(true)
    const resp = await this.childApi.sendDealBindingOfferEmail(this.root.sellerStore.item.username, this.item.token)
    this.toggleLoading(false)
    return resp
  }

  // we should use just payment session show
  @action fetchPSManage = async (reqData: { expand: string[] }, username?: string, accessToken?: string) => {
    const resp = await this.childApi.manageOrder(
      this.root.sellerStore.item.username || username,
      this.data.token,
      reqData,
      accessToken
    )
    const { data, success } = resp || {}

    if (success) {
      this.data = {
        ...this.data,
        ...data,
      }

      await this.root.sellerStore.setLockingByToken(this.data.protectionUrl)
    }

    this.setLoading(false)

    return resp
  }

  redirect = (redirectLink: string) => (window.location.href = redirectLink)

  isSetupIntentPossibleFor = (paymentForm: string): boolean =>
    isSetupIntentPossible(this.data?.setupIntentPossible, paymentForm)

  @action chargePS = async (
    data?: { amount?: number; toPay?: string; transfer?: any },
    stripeClient?: Stripe,
    stripeElement?: StripeElements
  ) => {
    const { amount, ...rest } = data || {}

    this.setLoading(true)
    this.purchaseError = ''

    const chargeOrderData = {
      ...rest,
    }

    const isCard = this.data.paymentForm === PAYMENT_FORMS.card
    const isMangoPay = this.data.moneyHolder.cardProvider === PAYMENT_PROVIDERS.mangoPay

    if (isCard && isMangoPay) {
      chargeOrderData.transfer = getMP3dsBrowserMetaData()
    }

    if (this.fraudSessionIdentifier) {
      const paypalFraudData = {
        paypalFraudToken: this.fraudSessionIdentifier,
      }

      chargeOrderData.transfer = {
        ...chargeOrderData.transfer,
        ...paypalFraudData,
      }
    }

    if (methodsToSubmit.includes(this.data.paymentForm) && stripeElement) {
      const { error: submitError } = await (stripeElement as StripeElements).submit()
      if (submitError) {
        this.setPurchaseError(submitError.message || I18n.t('react.shared.notify.error'))
        this.hideLoading()
        return
      }
    }

    const resp = await this.childApi.chargeOrder(this.root.sellerStore.item.username, this.data.token, chargeOrderData)
    const { error, redirectLink, clientSecret, paymentMethod, success, setupIntentSecret } = resp

    if (success) {
      const isP24 = this.data.paymentForm === PAYMENT_FORMS.p24
      const isIdeal = this.data.paymentForm === PAYMENT_FORMS.ideal
      const isPayByBank = this.data.paymentForm === PAYMENT_FORMS.payByBank
      const isStripeCard = this.data.paymentForm === PAYMENT_FORMS.card
      const isDigitalPayment =
        this.data.paymentForm === PAYMENT_FORMS.applePay || this.data.paymentForm === PAYMENT_FORMS.googlePay
      const isTwintSetup =
        this.data.paymentForm === PAYMENT_FORMS.twint && this.isSetupIntentPossibleFor(PAYMENT_FORMS.twint)
      const shouldHandleStripe =
        redirectLink &&
        (clientSecret || ((isStripeCard || isDigitalPayment || isTwintSetup) && setupIntentSecret)) &&
        stripeClient

      const shouldHandleDigital = isDigitalPayment && !!redirectLink && (!!clientSecret || !!setupIntentSecret)

      if (shouldHandleStripe && (isP24 || isIdeal || isPayByBank)) {
        handleStripePaymentElementTransaction(
          stripeElement,
          stripeClient,
          {
            billingDetails: this.getBillingDetails(),
            redirectLink: redirectLink,
          },
          clientSecret
        )
          .then((result) => {
            if (result.error) {
              this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
              this.hideLoading()
            }
          })
          .catch(() => {
            this.setPurchaseError(I18n.t('react.shared.notify.error'))
            this.hideLoading()
          })
      }

      if (shouldHandleStripe && (isStripeCard || isTwintSetup)) {
        if (paymentMethod) {
          if (clientSecret) {
            handleAdditionalConfirmation(
              stripeClient,
              () => this.redirect(redirectLink),
              () => this.setLoading(false),
              clientSecret,
              { id: paymentMethod },
              this.getBillingDetails()
            )
          } else if (setupIntentSecret) {
            handleStripePaymentElementSetup(
              stripeElement as StripeElements,
              stripeClient,
              {
                redirectLink,
                billingDetails: this.getBillingDetails(),
              },
              setupIntentSecret
            )
              .then((result) => {
                if (result.error) {
                  this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
                  this.hideLoading()
                  return
                }
                handleTransactionResponse(
                  result,
                  () => this.redirect(redirectLink),
                  () => this.setLoading(false)
                )
              })
              .catch(() => {
                this.setPurchaseError(I18n.t('react.shared.notify.error'))
                this.hideLoading()
              })
          } else {
            handleStripePaymentElementTransaction(
              stripeElement as StripeElements,
              stripeClient,
              {
                redirectLink,
                billingDetails: this.getBillingDetails(),
              },
              clientSecret
            )
              .then((result) => {
                if (result.error) {
                  this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
                  this.hideLoading()
                  return
                }
                handleTransactionResponse(
                  result,
                  () => this.redirect(redirectLink),
                  () => this.setLoading(false)
                )
              })
              .catch(() => {
                this.setPurchaseError(I18n.t('react.shared.notify.error'))
                this.hideLoading()
              })
          }
        } else {
          this.setStripePaymentParams({
            redirectLink,
            clientSecret,
            payerFullName: this.data.payer.fullName,
            payerCountryCode: this.data.payer.countryCode || this.data.payer.country?.code,
            payerEmail: this.data.payer.email,
            amount,
            setupIntentSecret,
          })
          this.setLoading(false)
          this.togglePopup('autopaySecondStep')
        }
      }

      if (shouldHandleDigital) {
        if (paymentMethod && stripeClient) {
          let setupIntent = null

          if (!clientSecret && setupIntentSecret) {
            try {
              const retrieveResult = await stripeClient.retrieveSetupIntent(setupIntentSecret)

              if (retrieveResult.error) {
                this.setPurchaseError(I18n.t('react.shared.notify.error'))
                this.setLoading(false)
                return
              }

              setupIntent = retrieveResult.setupIntent
            } catch {
              this.setPurchaseError(I18n.t('react.shared.notify.error'))
              this.setLoading(false)
              return
            }
          }

          if (setupIntent?.status === 'succeeded') {
            /* The wallet method is saved — the charge itself has to happen off-session on the backend */
            this.redirect(redirectLink)
          } else {
            handleStripeCardTransaction(
              stripeClient,
              { id: paymentMethod },
              clientSecret,
              this.getBillingDetails(),
              /* with both secrets present the PaymentIntent is the one to confirm */
              clientSecret ? null : setupIntentSecret
            ).then((result) =>
              handleTransactionResponse(
                result,
                () => this.redirect(redirectLink),
                () => this.setLoading(false)
              )
            )
          }
        } else {
          this.setStripePaymentParams({
            redirectLink,
            clientSecret,
            payerFullName: this.data.payer.fullName,
            payerCountryCode: this.data.payer.countryCode || this.data.payer.country?.code,
            payerEmail: this.data.payer.email,
            amount,
            setupIntentSecret,
          })
          this.setLoading(false)
          this.togglePopup('autopaySecondStep')
        }
      }

      if (
        (!shouldHandleStripe && !shouldHandleDigital) ||
        !(isP24 || isStripeCard || isIdeal || isDigitalPayment || isPayByBank || isTwintSetup)
      ) {
        this.redirect(redirectLink)
      }
    } else if (error) {
      const errorMessage =
        typeof error === 'string' ? error : (error as Error)?.message || I18n.t('react.shared.notify.error')
      this.setPurchaseError(errorMessage)
      this.setLoading(false)
    }
  }

  @action cancelPS = async () => {
    this.setLoading(true)
    const resp = await this.childApi.cancelOrder(this.root.sellerStore.item.username, this.data.token)
    const { data, success } = resp

    if (success) {
      notify('success', I18n.t('react.shop.order.canceled_successfully'))
      this.data = {
        ...this.data,
        ...data,
      }
    }
    this.setLoading(false)
  }

  @action updateOrder = async (postData) => {
    this.setLoading(true)
    const resp = await this.childApi.updateItem(
      { username: this.root.sellerStore.item.username, token: this.data.token },
      postData
    )
    const { data, success } = resp

    if (success) {
      notify('success', I18n.t('react.shared.notify.success'))
      this.data = {
        ...this.data,
        ...data,
      }
    }
    this.setLoading(false)
  }

  @action continuePS = async () => {
    this.setLoading(true)
    const resp = await this.childApi.continueOrder(this.root.sellerStore.item.username, this.data.token)
    const { data, success } = resp

    if (success) {
      notify('success', I18n.t('react.shop.order.renewed_successfully'))
      this.data = {
        ...this.data,
        ...data,
      }
    }
    this.setLoading(false)
  }

  submitMangoPayForm = () => {
    if (this.mangoPayForm) {
      try {
        this.mangoPayForm.disable()
        this.mangoPayForm.completePayment()
      } finally {
        this.mangoPayForm.enable()
      }
    }
  }

  @action changePaymentMethod = async (
    stripeClient: Stripe,
    stripeElement?: StripeElements | StripeExpressCheckoutElement,
    newPaymentForm?: string
  ) => {
    const { moneyHolder } = this.data || {}
    const isCard = newPaymentForm === PAYMENT_FORMS.card
    const isMangoPay = moneyHolder.cardProvider === PAYMENT_PROVIDERS.mangoPay

    if (isCard && isMangoPay) {
      this.submitMangoPayForm()
      return
    }

    return await this.changePSMethod(stripeClient, stripeElement, newPaymentForm)
  }

  isBillingDetailsValid = () => {
    const billingAddress = this.getBillingDetails()

    if (!billingAddress) return false

    const { address, email, name } = billingAddress
    const { city, country, postal_code, line1, state } = address
    const stateExisted = COUNTRIES_WITH_STATES.includes(country) ? !!state : true

    return Boolean(email && name && city && country && postal_code && line1 && stateExisted)
  }

  getBillingDetails(): StripeBillingDetails {
    const payerData = this.data.payerData

    if (!payerData?.userProfileAttributes || !payerData?.userAttributes) {
      return {
        name: '',
        email: '',
        phone: '',
        address: {
          city: '',
          country: '',
          postal_code: '',
          state: '',
          line1: '',
          line2: '',
        },
      }
    }

    const { userProfileAttributes, userAttributes } = payerData

    return {
      name: `${userProfileAttributes.firstName || ''} ${userProfileAttributes.lastName || ''}`.trim(),
      email: userAttributes.email || '',
      phone: userProfileAttributes.phone || '',
      address: {
        city: userProfileAttributes.city || '',
        country: toAlpha2CountryCode(userProfileAttributes.countryCode),
        postal_code: userProfileAttributes.zip || '',
        state: userProfileAttributes.state || '',
        line1: userProfileAttributes.street || '',
        line2: userProfileAttributes.streetNumber || '',
      },
    }
  }

  @action changePSMethod = async (
    stripeClient: Stripe,
    stripeElement?: StripeElements | StripeExpressCheckoutElement,
    newPaymentForm?: string
  ) => {
    const { moneyHolder, paymentForm, token } = this.data || {}

    this.setLoading(true)
    this.purchaseError = ''
    const currentPaymentForm = newPaymentForm || paymentForm
    const isCard = currentPaymentForm === PAYMENT_FORMS.card
    const isSepa = (newPaymentForm || paymentForm) === PAYMENT_FORMS.sepa
    const isMangoPay = moneyHolder.cardProvider === PAYMENT_PROVIDERS.mangoPay
    const isMangoPayCardFlow = isCard && isMangoPay

    const changeOrderMethodData = {
      ...this.paymethodsData,
    }

    if (isCard && isMangoPay) {
      const mp3dsBrowserMetaData = getMP3dsBrowserMetaData()

      changeOrderMethodData.transfer = {
        ...changeOrderMethodData.transfer,
        ...mp3dsBrowserMetaData,
      }
    }

    if (this.fraudSessionIdentifier) {
      const paypalFraudData = {
        paypalFraudToken: this.fraudSessionIdentifier,
      }

      changeOrderMethodData.transfer = {
        ...changeOrderMethodData.transfer,
        ...paypalFraudData,
      }
    }

    if (methodsToSubmit.includes(newPaymentForm) && stripeElement && 'submit' in stripeElement) {
      const { error: submitError } = await stripeElement.submit()
      if (submitError) {
        this.setPurchaseError(submitError.message || I18n.t('react.shared.notify.error'))
        this.hideLoading()
        return
      }
    }

    if (isSepa && stripeElement && 'submit' in stripeElement) {
      const payerParams = {
        payment_method_data: {
          billing_details: this.getBillingDetails(),
        },
      }

      // Create the ConfirmationToken using the details collected by the Payment Element
      const { error, confirmationToken } = await stripeClient.createConfirmationToken({
        elements: stripeElement,
        params: payerParams,
      })

      if (error) {
        this.setPurchaseError(error.message || I18n.t('react.shared.notify.error'))
        this.hideLoading()
        return
      }

      if (!confirmationToken || !confirmationToken.id) {
        Sentry.captureException({
          name: `Manage page: ${currentPaymentForm} confirmationToken error`,
          stipeElements: stripeElement,
          payerParams,
        })
        this.setPurchaseError(I18n.t('react.shared.notify.error'))
        this.hideLoading()
        return
      }

      const tokenObject = { confirmationToken: confirmationToken.id }

      changeOrderMethodData.transfer = {
        ...changeOrderMethodData.transfer,
        ...tokenObject,
      }
    }

    const resp = await this.childApi.changeOrderMethod(
      this.root.sellerStore.item.username,
      token,
      changeOrderMethodData
    )
    const { data: respData, redirectLink, clientSecret, success, setupIntentSecret } = resp

    if (success) {
      notify('success', I18n.t('react.shop.order.method_changed_success'))

      if (isMangoPayCardFlow) {
        return resp
      }

      const isP24 = respData.paymentForm === PAYMENT_FORMS.p24
      const isIdeal = respData.paymentForm === PAYMENT_FORMS.ideal
      const isPayByBank = respData.paymentForm === PAYMENT_FORMS.payByBank
      const isStripeCard = respData.paymentForm === PAYMENT_FORMS.card
      const isStripeSofort =
        respData.paymentForm === PAYMENT_FORMS.sofort &&
        (this.root.sellerStore.item.sofortProvider === PAYMENT_PROVIDERS.stripe ||
          this.root.sellerStore.item.sofortProvider === PAYMENT_PROVIDERS.elopageConnect)
      const isTwintSetup =
        respData.paymentForm === PAYMENT_FORMS.twint && this.isSetupIntentPossibleFor(PAYMENT_FORMS.twint)

      const shouldHandleStripe =
        redirectLink &&
        (clientSecret || ((isStripeCard || this.isDigitalPayment || isTwintSetup) && setupIntentSecret)) &&
        stripeClient

      if (shouldHandleStripe && (isP24 || isIdeal || isPayByBank)) {
        handleStripePaymentElementTransaction(
          stripeElement as StripeElements,
          stripeClient,
          {
            billingDetails: this.getBillingDetails(),
            redirectLink,
          },
          clientSecret
        )
          .then((result) => {
            if (result.error) {
              this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
              this.hideLoading()
            }
          })
          .catch(() => {
            this.setPurchaseError(I18n.t('react.shared.notify.error'))
            this.hideLoading()
          })
      }

      if (shouldHandleStripe && (isStripeCard || isTwintSetup)) {
        if (setupIntentSecret && clientSecret) {
          handleAdditionalConfirmation(
            stripeClient,
            () => this.redirect(redirectLink),
            () => this.setLoading(false),
            clientSecret,
            {},
            this.getBillingDetails()
          )
        } else if (setupIntentSecret) {
          handleStripePaymentElementSetup(
            stripeElement as StripeElements,
            stripeClient,
            {
              redirectLink,
              billingDetails: this.getBillingDetails(),
            },
            setupIntentSecret
          )
            .then((result) => {
              if (result.error) {
                this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
                this.hideLoading()
                return
              }
              handleTransactionResponse(
                result,
                () => this.redirect(redirectLink),
                () => this.setLoading(false)
              )
            })
            .catch(() => {
              this.setPurchaseError(I18n.t('react.shared.notify.error'))
              this.hideLoading()
            })
        } else {
          handleStripePaymentElementTransaction(
            stripeElement as StripeElements,
            stripeClient,
            {
              redirectLink,
              billingDetails: this.getBillingDetails(),
            },
            clientSecret
          )
            .then((result) => {
              if (result.error) {
                this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
                this.hideLoading()
                return
              }
              handleTransactionResponse(
                result,
                () => this.redirect(redirectLink),
                () => this.setLoading(false)
              )
            })
            .catch(() => {
              this.setPurchaseError(I18n.t('react.shared.notify.error'))
              this.hideLoading()
            })
        }
      }

      if (shouldHandleStripe && this.isDigitalPayment) {
        return resp
      }

      if (
        !shouldHandleStripe ||
        !(isP24 || isStripeCard || isStripeSofort || isIdeal || isPayByBank || this.isDigitalPayment || isTwintSetup)
      ) {
        if (redirectLink) {
          this.popups.changeMethod = false
          this.redirect(redirectLink)
        } else {
          this.popups.changeMethod = false
          this.data = {
            ...this.data,
            ...respData,
          }
        }
        this.setLoading(false)
      }
    } else {
      const errorMessage =
        typeof resp?.error === 'string' ? resp.error : resp?.error?.message || I18n.t('react.shared.notify.error')
      this.setPurchaseError(errorMessage)
      this.hideLoading()
      if (isMangoPayCardFlow) {
        return resp
      }
    }
  }

  handleDigitalExpressClick = (submitData: ClickSubmitData) => {
    const showDigitalPaymentModal =
      this.isDigitalPayment && submitData.stripeClient && submitData.expressElement && this.hasDebtRates

    if (showDigitalPaymentModal) {
      submitData.showPopup()
    } else {
      this.changePSPlan(submitData.stripeClient, submitData.expressElement)
    }
  }

  @action changePSPlan = async (
    stripeClient?: Stripe,
    stripeElement?: StripeElements | StripeExpressCheckoutElement
  ) => {
    const { moneyHolder, payer, paymentForm, token } = this.data || {}

    this.setLoading(true)
    this.purchaseError = ''

    const chargeOrderData: ChargeOrderData = {
      pricingPlanId: this.selectedPlanId,
    }

    const isCard = paymentForm === PAYMENT_FORMS.card
    const isMangoPay = moneyHolder.cardProvider === PAYMENT_PROVIDERS.mangoPay
    const isPayByBank = paymentForm === PAYMENT_FORMS.payByBank
    if (isCard && isMangoPay) {
      chargeOrderData.transfer = getMP3dsBrowserMetaData()
    }

    if (this.fraudSessionIdentifier) {
      const paypalFraudData = {
        paypalFraudToken: this.fraudSessionIdentifier,
      }

      chargeOrderData.transfer = {
        ...(chargeOrderData?.transfer || {}),
        ...paypalFraudData,
      }
    }

    if (methodsToSubmit.includes(paymentForm) && stripeElement) {
      const { error: submitError } = await (stripeElement as StripeElements).submit()
      if (submitError) {
        this.setPurchaseError(submitError.message || I18n.t('react.shared.notify.error'))
        this.hideLoading()
        return
      }
    }

    const resp = await this.childApi.changeOrderPlan(this.root.sellerStore.item.username, token, chargeOrderData)
    const { data, redirectLink, clientSecret, paymentMethod, success, setupIntentSecret } = resp

    if (success) {
      notify('success', I18n.t('react.shop.order.plan_changed_success'))

      const isP24 = paymentForm === PAYMENT_FORMS.p24
      const isIdeal = paymentForm === PAYMENT_FORMS.ideal

      const isStripeCard = paymentForm === PAYMENT_FORMS.card
      const isStripeSofort =
        paymentForm === PAYMENT_FORMS.sofort &&
        (this.root.sellerStore.item.sofortProvider === PAYMENT_PROVIDERS.stripe ||
          this.root.sellerStore.item.sofortProvider === PAYMENT_PROVIDERS.elopageConnect)
      const isTwintSetup = paymentForm === PAYMENT_FORMS.twint && this.isSetupIntentPossibleFor(PAYMENT_FORMS.twint)
      const shouldHandleStripe =
        redirectLink &&
        (clientSecret || ((isStripeCard || this.isDigitalPayment || isTwintSetup) && setupIntentSecret)) &&
        stripeClient

      if (shouldHandleStripe && (isP24 || isIdeal || isPayByBank)) {
        handleStripePaymentElementTransaction(
          stripeElement as StripeElements,
          stripeClient,
          {
            billingDetails: this.getBillingDetails(),
            redirectLink,
          },
          clientSecret
        )
          .then((result) => {
            if (result.error) {
              this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
              this.hideLoading()
            }
          })
          .catch(() => {
            this.setPurchaseError(I18n.t('react.shared.notify.error'))
            this.hideLoading()
          })
      }

      if (shouldHandleStripe && (isStripeCard || this.isDigitalPayment || isTwintSetup)) {
        if (paymentMethod) {
          if (isStripeCard || isTwintSetup) {
            if (setupIntentSecret && clientSecret) {
              handleAdditionalConfirmation(
                stripeClient,
                () => this.redirect(redirectLink),
                () => this.setLoading(false),
                clientSecret,
                { id: paymentMethod },
                this.getBillingDetails()
              )
            } else if (setupIntentSecret) {
              handleStripePaymentElementSetup(
                stripeElement as StripeElements,
                stripeClient,
                {
                  redirectLink,
                  billingDetails: this.getBillingDetails(),
                  id: paymentMethod,
                },
                setupIntentSecret
              )
                .then((result) => {
                  if (result.error) {
                    this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
                    this.hideLoading()
                    return
                  }
                  handleTransactionResponse(
                    result,
                    () => this.redirect(redirectLink),
                    () => this.setLoading(false)
                  )
                })
                .catch(() => {
                  this.setPurchaseError(I18n.t('react.shared.notify.error'))
                  this.hideLoading()
                })
            } else {
              handleStripePaymentElementTransaction(
                stripeElement as StripeElements,
                stripeClient,
                {
                  redirectLink,
                  billingDetails: this.getBillingDetails(),
                  id: paymentMethod,
                },
                clientSecret
              )
                .then((result) => {
                  if (result.error) {
                    this.setPurchaseError(result.error.message || I18n.t('react.shared.notify.error'))
                    this.hideLoading()
                    return
                  }
                  handleTransactionResponse(
                    result,
                    () => this.redirect(redirectLink),
                    () => this.setLoading(false)
                  )
                })
                .catch(() => {
                  this.setPurchaseError(I18n.t('react.shared.notify.error'))
                  this.hideLoading()
                })
            }
          }
          if (this.isDigitalPayment) {
            return resp
          }
        } else {
          this.setStripePaymentParams({
            redirectLink,
            clientSecret,
            payerFullName: payer.fullName,
            payerCountryCode: payer.countryCode || payer.country?.code,
            payerEmail: payer.email,
            setupIntentSecret,
          })
          this.setLoading(false)
          this.togglePopup('autopaySecondStep')
        }
      }

      if (
        !shouldHandleStripe ||
        !(isP24 || isIdeal || isPayByBank || isStripeCard || isStripeSofort || this.isDigitalPayment || isTwintSetup)
      ) {
        if (redirectLink) {
          this.popups.changePlan = false
          this.redirect(redirectLink)
        } else {
          this.popups.changePlan = false
          this.popups.changePlanAutopay = false
          this.data = {
            ...this.data,
            ...data,
          }
        }
      }
    } else {
      const errorMessage =
        typeof resp?.error === 'string' ? resp.error : resp?.error?.message || I18n.t('react.shared.notify.error')
      this.setPurchaseError(errorMessage)
    }

    this.setLoading(false)
  }

  @action resendEmail = (username: string, orderToken: string) => this.childApi.resendEmail(username, orderToken)

  @computed get hasDebtRates() {
    const { nextChargeAmount, nextChargeDate } = this.data || {}
    return nextChargeAmount && nextChargeDate < new Date().toISOString()
  }

  @computed get isDigitalPayment() {
    const { form } = this.paymethods || {}
    return form === PAYMENT_FORMS.applePay || form === PAYMENT_FORMS.googlePay
  }

  //Postsell's functions

  @computed get lastOrder() {
    return this.list[this.list.length - 1] || ({} as Order)
  }

  @computed get firstOrder() {
    return this.list[0] || ({} as Order)
  }

  @computed get currency() {
    return this.root.currenciesStore.getKey(this.data?.sellableWithPeriod?.pricingPlan?.currencyId) || DEFAULT_CURRENCY
  }

  totalPrice() {
    return this.stripePaymentParams?.amount || this.data?.nextChargeAmount || 0
  }

  getSepaStripeElementPayload = (): StripeElementsOptionsMode => {
    const { sepaProvider, elopageConnectAccountId } = this.root.sellerStore.item

    return {
      mode: 'setup',
      paymentMethodCreation: 'manual',
      paymentMethodTypes: ['sepa_debit'],
      currency: this.currency,
      locale: getStripeLocale(),
      setupFutureUsage: 'off_session',
      ...(sepaProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getKlarnaStripeElementPayload = (): StripeElementsOptionsMode => {
    const { klarnaProvider, elopageConnectAccountId } = this.root.sellerStore.item

    const klarnaParams = {
      mode: 'payment',
      paymentMethodTypes: [PAYMENT_FORMS.klarna],
      currency: this.currency,
      amount: convertPriceForStripe(this.totalPrice()),
      locale: getStripeLocale(),
      ...(klarnaProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }

    this.kibanaApi.reportKlarnaParams(this.root.sellerStore?.item?.id, {
      ...klarnaParams,
      source: 'orders',
    })

    return klarnaParams as StripeElementsOptionsMode
  }

  getCurrencyAndAmount = () => ({
    currency: this.currency,
    amount: convertPriceForStripe(this.totalPrice()),
  })

  getStripeElementOptions = (): StripeElementsOptionsMode => ({
    mode: 'payment',
    locale: getStripeLocale(),
    ...this.getCurrencyAndAmount(),
  })

  getTwintStripeElementPayload = (mode?: string): StripeElementsOptionsMode => {
    const { twintProvider, elopageConnectAccountId } = this.root.sellerStore.item

    const data =
      mode === PAYMENT_ELEMENTS_MODE.setup
        ? {
            mode: PAYMENT_ELEMENTS_MODE.setup,
            currency: this.currency,
            locale: getStripeLocale(),
          }
        : this.getStripeElementOptions()

    const savesMandate = mode === PAYMENT_ELEMENTS_MODE.setup || isNotOneTimePlanForm(this.data?.periodType)

    return {
      ...data,
      ...(savesMandate ? { setupFutureUsage: 'off_session' } : {}),
      paymentMethodTypes: [PAYMENT_FORMS.twint],
      ...(twintProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getMonduStripeElementPayload = (): StripeElementsOptionsMode => ({
    ...this.getStripeElementOptions(),
    paymentMethodTypes: [PAYMENT_FORMS.mondu],
  })

  getPayByBankStripeElementPayload = (): StripeElementsOptionsMode => {
    const { payByBankProvider, elopageConnectAccountId } = this.root.sellerStore.item
    return {
      ...this.getStripeElementOptions(),
      paymentMethodTypes: [PAYMENT_FORMS.payByBank],
      ...(payByBankProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getStripeCardElementPayload = (mode?: string): StripeElementsOptionsMode => {
    const { cardProvider, elopageConnectAccountId } = this.root.sellerStore.item

    const data =
      mode === 'setup'
        ? {
            mode: 'setup' as const,
            currency: this.currency,
            locale: getStripeLocale(),
          }
        : this.getStripeElementOptions()

    return {
      ...data,
      paymentMethodTypes: [PAYMENT_FORMS.card],
      ...(cardProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
      setupFutureUsage: 'off_session',
    }
  }

  getP24StripeElementPayload = (): StripeElementsOptionsMode => {
    const { p24Provider, elopageConnectAccountId } = this.root.sellerStore.item
    return {
      ...this.getStripeElementOptions(),
      paymentMethodTypes: ['p24'],
      ...(p24Provider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getIdealStripeElementPayload = (mode: string): StripeElementsOptionsMode => {
    const { idealProvider, elopageConnectAccountId } = this.root.sellerStore.item

    const data =
      mode === PAYMENT_ELEMENTS_MODE.setup
        ? {
            mode: PAYMENT_ELEMENTS_MODE.setup,
            currency: this.currency,
            locale: getStripeLocale(),
          }
        : this.getStripeElementOptions()

    return {
      ...data,
      ...(mode === PAYMENT_ELEMENTS_MODE.setup ? { setupFutureUsage: 'off_session' } : {}),
      paymentMethodTypes: [PAYMENT_FORMS.ideal],
      ...(idealProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getStripeElementPayload = (paymentForm: string, mode?: string): StripeElementsOptionsMode => {
    switch (paymentForm) {
      case PAYMENT_FORMS.card:
        return this.getStripeCardElementPayload(mode)
      case PAYMENT_FORMS.p24:
        return this.getP24StripeElementPayload()
      case PAYMENT_FORMS.ideal:
        return this.getIdealStripeElementPayload(mode)
      case PAYMENT_FORMS.payByBank:
        return this.getPayByBankStripeElementPayload()
      case PAYMENT_FORMS.klarna:
        return this.getKlarnaStripeElementPayload()
      case PAYMENT_FORMS.twint:
        return this.getTwintStripeElementPayload(mode)
      case PAYMENT_FORMS.mondu:
        return this.getMonduStripeElementPayload()
      default:
        return {} as StripeElementsOptionsMode
    }
  }

  getCurrency = () => this.root.currenciesStore.getKey(this.data?.sellableWithPeriod?.pricingPlan?.currencyId)

  preparePostsell = async (params?: { funnel: string }) => {
    const { error } = this.item
    const {
      item: { username },
    } = this.root.sellerStore
    const { paymentForm, token, funnelId } = this.lastOrder

    const funnelOn = params?.funnel === 'on'
    const rootOrderState = this.firstOrder.paymentState
    const orderPaymentIsSuccessful = SUCCESSFUL_ORDER_STATES.find((state) => state === rootOrderState)
    const showFunnel = funnelOn && paymentForm !== 'free' && !error && !!funnelId && orderPaymentIsSuccessful

    if (showFunnel) {
      const resp = await this.root.funnelsStore.fetchFunnel(username, token)

      return resp.data
    } else {
      this.root.funnelsStore.toggleLoading(false)
    }
  }

  onRejectPostsell = async () => {
    const { token } = this.lastOrder
    const {
      item: { username },
    } = this.root.sellerStore
    const { currentFunnelNode: node, rejectPostsell } = this.root.funnelsStore

    const resp = await rejectPostsell(username, token, { funnelNodeId: node?.id })

    if (this.root.funnelsStore.currentFunnelNode) {
      const url = `/${`shop/${username}`}/content_pages/${
        this.root.funnelsStore.currentFunnelNode.contentPageId
      }?screen_size=desktop`
      const data = await this.root.apiClient.GET<{ status: number; statusText: string; success: boolean; data: any }>(
        url
      )
      // @ts-ignore
      const productIds = (data?.data?.contentBlocks || [])
        .filter(({ children }) => children.find((item) => item.form === 'funnel_product'))
        .reduce((acc, { children }) => [...acc, ...children.map((item) => item?.content?.removeData?.productId)], [])
      // @ts-ignore
      const productNames = (data?.data?.contentBlocks || [])
        .filter(({ children }) => children.find((item) => item.form === 'funnel_product'))
        .reduce((acc, { children }) => [...acc, ...children.map((item) => item?.data?.product?.name)], [])

      await this.fireFunnelPageTrackings(resp.data, productIds, productNames)
    } else {
      await this.fireSuccessPageTrackings()
    }

    window.scrollTo(0, 0)
  }

  onSubmitPostsell = async (data) => {
    const { token } = this.lastOrder
    const {
      item: { username },
    } = this.root.sellerStore
    const { currentFunnelNode, acceptPostsell, setLoading, setNextFunnelNode } = this.root.funnelsStore
    const {
      moneyHolder,
      moneyHolder: {
        cardProvider,
        sofortProvider,
        sepaProvider,
        applePayProvider,
        googlePayProvider,
        p24Provider,
        idealProvider,
        klarnaProvider,
        twintProvider,
        monduProvider,
        payByBankProvider,
      } = {},
      paymentForm,
    } = this.data
    const isCard = paymentForm === PAYMENT_FORMS.card
    const isMangoPay = this.data.moneyHolder.cardProvider === PAYMENT_PROVIDERS.mangoPay

    const acceptPostsellParams = {
      funnelNodeId: currentFunnelNode?.id,
      ...data,
      ...(this.fraudSessionIdentifier && { paypalFraudToken: this.fraudSessionIdentifier }),
      ...(isCard && isMangoPay ? getMP3dsBrowserMetaData() : {}),
    }

    const isP24 = paymentForm === PAYMENT_FORMS.p24

    const isIdeal = paymentForm === PAYMENT_FORMS.ideal

    const isPayByBank = paymentForm === PAYMENT_FORMS.payByBank

    const isStripeCard =
      paymentForm === PAYMENT_FORMS.card &&
      (cardProvider === PAYMENT_PROVIDERS.stripe || cardProvider === PAYMENT_PROVIDERS.elopageConnect)

    const isDigitalPayment = paymentForm === PAYMENT_FORMS.applePay || paymentForm === PAYMENT_FORMS.googlePay

    const skipFunnelRedirect = isP24 || isIdeal || isPayByBank || isStripeCard || isDigitalPayment

    const resp = (await acceptPostsell(username, token, acceptPostsellParams, skipFunnelRedirect)) || {
      success: false,
      data: null,
    }
    const { success } = resp
    const { redirectLink, clientSecret, paymentMethod, setupIntentSecret } = resp.data || {}

    if (success) {
      const providers = {
        cardProvider,
        sofortProvider,
        sepaProvider,
        applePayProvider,
        googlePayProvider,
        p24Provider,
        idealProvider,
        klarnaProvider,
        twintProvider,
        monduProvider,
        payByBankProvider,
      }
      const pubKey = (moneyHolder || {})[getPubKey(paymentForm, providers)]

      if (redirectLink && (isP24 || isIdeal || isPayByBank) && clientSecret) {
        this.togglePopup('postsellPayment')
        this.setStripePaymentParams({
          redirectLink,
          clientSecret,
          payerFullName: this.data.payer.fullName,
          payerCountryCode: this.data.payer.countryCode || this.data.payer.country?.code,
          payerEmail: this.data.payer.email,
        })
        setLoading(false)
      } else if (
        (clientSecret || (isStripeCard && setupIntentSecret)) &&
        paymentMethod &&
        (isStripeCard || isDigitalPayment)
      ) {
        if (setupIntentSecret && clientSecret) {
          handleAdditionalConfirmation(
            null,
            () => {
              if (redirectLink) {
                this.redirect(redirectLink)
              } else {
                setNextFunnelNode(username, token, YES)
              }
            },
            () => setLoading(false),
            clientSecret,
            { id: paymentMethod },
            this.getBillingDetails(),
            pubKey
          )
        } else {
          handleStripeCardRecuringPayment(
            clientSecret,
            paymentMethod,
            pubKey,
            () => {
              if (redirectLink) {
                this.redirect(redirectLink)
              } else {
                setNextFunnelNode(username, token, YES)
              }
            },
            () => setLoading(false)
          )
        }
      } else if (redirectLink) {
        this.redirect(redirectLink)
      } else {
        await this.fetchItem(token, { username })
        await this.fetchList({ token, username })

        if (this.root.funnelsStore.currentFunnelNode) {
          const url = `/${`shop/${username}`}/content_pages/${
            this.root.funnelsStore.currentFunnelNode.contentPageId
          }?screen_size=desktop`
          const data = await this.root.apiClient.GET<{
            status: number
            statusText: string
            success: boolean
            data: any
          }>(url)
          // @ts-ignore
          const productIds = (data?.data?.contentBlocks || [])
            .filter(({ children }) => children.find((item) => item.form === 'funnel_product'))
            .reduce(
              (acc, { children }) => [...acc, ...children.map((item) => item?.content?.removeData?.productId)],
              []
            )
          // @ts-ignore
          const productNames = (data?.data?.contentBlocks || [])
            .filter(({ children }) => children.find((item) => item.form === 'funnel_product'))
            .reduce((acc, { children }) => [...acc, ...children.map((item) => item?.data?.product?.name)], [])
          await this.fireFunnelPageTrackings(this.root.funnelsStore.funnel, productIds, productNames)
        } else {
          await this.fireSuccessPageTrackings()
        }

        setLoading(false)
      }
    }

    window.scrollTo(0, 0)
  }

  fireFunnelPageTrackings = async (funnel, productIds = [], productNames = []) => {
    const funnelNode = this.root.funnelsStore.currentFunnelNode || funnel || {}

    this.root.trackingCodesStore.setTrackEventData('funnel_page', {
      productIds,
      productNames,
      username: this.root.sellerStore.item.username,
      consentForm: this.root.sellerStore.item.consentForm,
      funnelId: this.root.funnelsStore.funnelId,
      funnelName: this.root.funnelsStore.funnelName || '',
      funnelPageId: funnelNode?.id,
      funnelPageName: funnelNode?.name || '',
    })

    await this.root.trackingCodesStore.trackEvent()
  }

  fireSuccessPageTrackings = async () => {
    for (const item of this.list) {
      if (!item.tracked) {
        const { currencyKey } = item
        this.root.trackingCodesStore.setTrackEventData('success_page', {
          productId: item.product.id,
          sellerId: this.root.sellerStore.item.id,
          username: this.root.sellerStore.item.username,
          consentForm: this.root.sellerStore.item.consentForm,
          transactionId:
            (arrayLast<{ idForSeller: number }>(item.transfers) || {}).idForSeller ||
            (arrayLast<Invoice>(item.invoices) || {}).number,
          orderId: item.id,
          periodType: item.periodType,
          orderForm: item.paymentForm,
          revenue:
            (arrayLast<Transfer>(item.transfers) || {}).total || (arrayLast<Invoice>(item.invoices) || {}).total || 0,
          funnelId: item.funnelId,
          funnelName: item.funnelName,
          funnelNodeId: item.funnelNodeId,
          productIds: item.sellables.map((p) => p.product.id),
          productNames: item.sellables.map((p) => p.product.name),
          currency: currencyKey || DEFAULT_CURRENCY,
        })

        await this.root.trackingCodesStore.trackEvent()
      }
    }
  }

  mangoPayOnCreateCardRegistration = async () => {
    const orderResponse = await this.changePSMethod(null, null, PAYMENT_FORMS.card)

    if (!orderResponse.success) {
      //handled on api method level
      throw new Error('ablefy.error')
    }

    const cardRegistration = orderResponse.data.creditCard.mangoPay

    cardRegistration.id = cardRegistration.cardRegistrationId
    cardRegistration.currency = cardRegistration.currency.toUpperCase()
    cardRegistration.cardRegistrationURL = cardRegistration.cardRegistrationUrl

    return Object.keys(cardRegistration).reduce(
      (acc, key) => ({
        ...acc,
        [capitalizeFirstLetter(key)]: cardRegistration[key],
      }),
      {}
    ) as CreateCardRegistrationResult
  }

  mangoPayOnCreatePayment = async (event: { CardId: string }) => {
    const resp = await this.root.paymentStore.confirmMangoCard(this.data.token, event.CardId)

    if (resp.success) {
      setTimeout(() => {
        window.location.href = resp.data.redirectUrl
      }, 100)
    }

    return {}
  }

  //End Postsell's functions

  @computed get paymethodsData(): {
    transfer: { confirmationToken?: string; creditCard: string | { num: string }; form: string; iban: string }
  } {
    const { creditCard: paymethodCard, form, iban } = this.paymethods || {}
    const creditCard = paymethodCard

    return {
      transfer: {
        creditCard,
        form,
        iban,
      },
    }
  }

  markPsAsTracked = (username: string, orderId: number) => this.childApi.markPsAsTracked(username, orderId)

  @override
  hydrate(key, data) {
    if (key === 'item') {
      this.item = data
    } else if (key === 'list') {
      this.list = data
    } else if (key === 'data') {
      this.data = data
    } else if (key === 'loading') {
      this.loading = data
    } else if (key === 'ticketUpgradeData') {
      this.ticketUpgradeData = data
    }
  }
}
